// src/pages/ingresos/components/IntakeInstrumentModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../utils/supabase";
import {
  buildDuplicateInstrumentMessage,
  findInstrumentDuplicates,
} from "../../../utils/instrumentDuplicateGuard";
import SerialTagAutocomplete from "./SerialTagAutocomplete";
import CalibrationPlaceIcon from "./CalibrationPlaceIcon";
import { INSTRUMENT_TYPE_OPTIONS } from "../../../utils/instrument-types";
import {
  admissionValuesForExistingInstrument,
  toDateOnly,
} from "../../../utils/instrument-admission";

export default function IntakeInstrumentModal({
  open,
  onClose,
  intakeId,
  client,
  mode = "intake", // "intake" (alta+link) | "edit" (editar maestro)
  instrumentId, // sólo en modo "edit"
  servCode, // N° de instrumento dentro de la OS (SERV), sólo en modo "edit"
  onSaved, // callback cuando se guarda en modo edición
  criticalUnlocked = false,
  onRequestCriticalAccess,
  canChangeClient = true,
}) {
  const clientId = client?.id || null;
  const isEdit = mode === "edit";

  // estado general
  const [saving, setSaving] = useState(false);
  const [existingInstrument, setExistingInstrument] = useState(null);
  const [instrumentClientId, setInstrumentClientId] = useState("");
  const [originalClientId, setOriginalClientId] = useState("");
  const [allowClientChange, setAllowClientChange] = useState(false);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  const CRITICAL_ADMIN_PASSWORD =
    import.meta?.env?.VITE_CRITICAL_ADMIN_PASSWORD ||
    "CambioCriticoAdmin1998PN-4111";

  // campos
  const [tag, setTag] = useState("");
  const [serial, setSerial] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [bakerCode, setBakerCode] = useState("");

  const [type, setType] = useState("pressure");
  const [unit, setUnit] = useState("");
  const [rangeMin, setRangeMin] = useState("");
  const [rangeMax, setRangeMax] = useState("");
  const [resolution, setResolution] = useState("");
  const [tolerance, setTolerance] = useState("");
  const [isAnalog, setIsAnalog] = useState(false);
  const [calibrationIntervalMonths, setCalibrationIntervalMonths] = useState("");
  const [receptionDate, setReceptionDate] = useState(todayIsoDate());
  const [currentIntakeDate, setCurrentIntakeDate] = useState(todayIsoDate());
  const [departureDate, setDepartureDate] = useState("");
  const [calibrationPlace, setCalibrationPlace] = useState("Laboratorio");
  const [instrumentMetaSupported, setInstrumentMetaSupported] = useState(null);
  const [instrumentMetaDirty, setInstrumentMetaDirty] = useState({
    receptionDate: false,
    departureDate: false,
    calibrationPlace: false,
  });

  // ✅ NUEVO: observaciones del instrumento (persistentes)
  const [observations, setObservations] = useState("");
  const [obsDirty, setObsDirty] = useState(false);

  // nota para intake_items
  const [note, setNote] = useState("");

  // categoría real en BD
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [showTechnicalData, setShowTechnicalData] = useState(false);

  // 👉 listado de instrumentos ya cargados en este intake
  const [intakeItems, setIntakeItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemsError, setItemsError] = useState("");
  const [itemsRefreshKey, setItemsRefreshKey] = useState(0);

  const nullIfEmpty = (v) => {
    if (v == null) return null;
    const s = String(v).trim();
    return s === "" ? null : s;
  };

  function shouldRetryWithoutCalibrationMeta(error) {
    const msg = String(error?.message || "").toLowerCase();
    return (
      msg.includes("reception_date") ||
      msg.includes("calibration_place") ||
      msg.includes("departure_date")
    );
  }

  function todayIsoDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  useEffect(() => {
    if (!open) return;

    let active = true;
    (async () => {
      try {
        const { error } = await supabase
          .from("instruments")
          .select("reception_date, calibration_place, departure_date")
          .limit(1);
        if (active) setInstrumentMetaSupported(!error);
      } catch {
        if (active) setInstrumentMetaSupported(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open]);

  // La fecha del ingreso pertenece al comprobante/intake actual. No debe
  // recuperarse desde la ficha maestra de un instrumento que vuelve.
  useEffect(() => {
    if (!open || !intakeId || isEdit) return;
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("intakes")
          .select("created_at")
          .eq("id", intakeId)
          .single();
        if (error) throw error;
        if (!active) return;
        const date = toDateOnly(data?.created_at) || todayIsoDate();
        setCurrentIntakeDate(date);
        setReceptionDate(date);
        setDepartureDate("");
      } catch (e) {
        console.warn("[IntakeInstrumentModal] fecha del ingreso", e?.message || e);
        if (active) {
          const date = todayIsoDate();
          setCurrentIntakeDate(date);
          setReceptionDate(date);
          setDepartureDate("");
        }
      }
    })();
    return () => { active = false; };
  }, [open, intakeId, isEdit]);

  function formatBranchLabel(branchNo, branchName) {
    if (branchNo == null) return "";
    const bn = String(branchNo).trim();
    if (!bn) return "";
    const bname = (branchName || "").trim();
    return bname ? `Sucursal ${bn} - ${bname}` : `Sucursal ${bn}`;
  }

  function formatClientOption(c) {
    const cn = c.client_number != null ? String(c.client_number).trim() : "";
    const bNo = c.branch_no != null ? ` ${c.branch_no}` : "";
    const branch = formatBranchLabel(c.branch_no, c.branch_name);
    const branchExtra = branch ? ` - ${branch}` : "";
    return `${cn}${bNo} - ${c.name || "Sin nombre"}${branchExtra}`;
  }

  // opciones de “Variable” (compartidas con el buscador de certificados)
  const TYPE_OPTIONS = INSTRUMENT_TYPE_OPTIONS;

  const categoriesForType = useMemo(
    () =>
      categories.filter((category) => {
        const allowed = category.variable_types || [];
        return (
          allowed.length === 0 ||
          allowed.includes(type) ||
          category.id === categoryId
        );
      }),
    [categories, type, categoryId]
  );

  function selectCategory(nextCategoryId) {
    setCategoryId(nextCategoryId);
  }

  function parseNum(s) {
    if (s === "" || s === null || s === undefined) return null;
    const n = Number(String(s).replace(",", "."));
    return Number.isNaN(n) ? null : n;
  }

  function localCriticalPromptUnlock() {
    const pass = window.prompt("Contrasena de cambio critico:");
    if (pass == null) return false;
    if (String(pass) !== String(CRITICAL_ADMIN_PASSWORD)) {
      alert("Contrasena incorrecta.");
      return false;
    }
    return true;
  }

  async function fetchInstrumentObsIfNeeded(inst) {
    // Si ya viene en el objeto, usalo.
    if (inst && Object.prototype.hasOwnProperty.call(inst, "observations")) {
      setObservations(inst.observations || "");
      setObsDirty(false);
      return;
    }
    // Si no viene, hacemos un fetch mínimo por id
    if (!inst?.id) {
      setObservations("");
      setObsDirty(false);
      return;
    }
    try {
      const selectFields =
        instrumentMetaSupported === true
          ? "observations, category_id, reception_date, departure_date, calibration_place"
          : "observations, category_id";
      const { data, error } = await supabase
        .from("instruments")
        .select(selectFields)
        .eq("id", inst.id)
        .single();
      if (error) throw error;
      setObservations(data?.observations || "");
      if (!inst?.category_id) setCategoryId(data?.category_id || "");
      setObsDirty(false);
    } catch (e) {
      console.error("[IntakeInstrumentModal] fetch observations", e);
      // no frenamos el flujo
      setObservations("");
      setObsDirty(false);
    }
  }

  // limpiar formulario cuando se abre en modo intake
  useEffect(() => {
    if (!open || isEdit) return;

    setSaving(false);
    setExistingInstrument(null);

    setTag("");
    setSerial("");
    setBrand("");
    setModel("");
    setBakerCode("");

    setType("pressure");
    setUnit("");
    setRangeMin("");
    setRangeMax("");
    setResolution("");
    setTolerance("");
    setIsAnalog(false);
    setCalibrationIntervalMonths("");

    setObservations("");
    setObsDirty(false);
    setReceptionDate(todayIsoDate());
    setDepartureDate("");
    setCalibrationPlace("Laboratorio");
    setInstrumentMetaDirty({
      receptionDate: false,
      departureDate: false,
      calibrationPlace: false,
    });
    setShowTechnicalData(false);

    setNote("");
    setCategoryId("");
  }, [open, isEdit]);

  // cargar categorías reales desde BD
  useEffect(() => {
    if (!open) return;

    let active = true;
    async function loadCategories() {
      try {
        const { data, error } = await supabase
          .from("instrument_categories")
          .select("id, name, variable_types")
          .order("name", { ascending: true });
        if (!active) return;
        if (error) throw error;
        setCategories(data || []);
      } catch (e) {
        console.error("[IntakeInstrumentModal] load categories", e);
        if (active) setCategories([]);
      }
    }

    loadCategories();
    window.addEventListener("instrument-categories-changed", loadCategories);
    return () => {
      active = false;
      window.removeEventListener(
        "instrument-categories-changed",
        loadCategories
      );
    };
  }, [open]);

  // cargar listado de empresa/sucursal para reasignar instrumento (modo edición)
  useEffect(() => {
    if (!open || !isEdit) return;
    let active = true;

    (async () => {
      try {
        setLoadingClients(true);
        const { data, error } = await supabase
          .from("clients")
          .select("id, name, client_number, branch_no, branch_name")
          .order("client_number", { ascending: true })
          .order("branch_no", { ascending: true, nullsFirst: true })
          .order("name", { ascending: true });
        if (!active) return;
        if (error) throw error;
        setClients(data || []);
      } catch (e) {
        console.error("[IntakeInstrumentModal] load clients", e);
        if (active) setClients([]);
      } finally {
        if (active) setLoadingClients(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, isEdit]);

  // cuando se elige un instrumento existente, rellenar campos
  useEffect(() => {
    if (!existingInstrument) return;

    setTag(existingInstrument.tag || "");
    setSerial(existingInstrument.serial || "");
    setBrand(existingInstrument.brand || "");
    setModel(existingInstrument.model || "");
    setBakerCode(existingInstrument.baker_code || "");
    setType(existingInstrument.type || "pressure");
    setUnit(existingInstrument.unit || "");
    setRangeMin(
      existingInstrument.range_min != null
        ? String(existingInstrument.range_min)
        : ""
    );
    setRangeMax(
      existingInstrument.range_max != null
        ? String(existingInstrument.range_max)
        : ""
    );
    setResolution(
      existingInstrument.resolution != null
        ? String(existingInstrument.resolution)
        : ""
    );
    setTolerance(
      existingInstrument.tolerance != null
        ? String(existingInstrument.tolerance)
        : ""
    );
    setIsAnalog(!!existingInstrument.is_analog);
    setCalibrationIntervalMonths(
      existingInstrument.calibrationintervalmonths != null
        ? String(existingInstrument.calibrationintervalmonths)
        : ""
    );
    setCategoryId(existingInstrument.category_id || "");
    const admission = admissionValuesForExistingInstrument(existingInstrument, {
      receptionDate: currentIntakeDate,
      calibrationPlace: "Laboratorio",
    });
    setReceptionDate(admission.receptionDate);
    setDepartureDate(admission.departureDate);
    setCalibrationPlace(admission.calibrationPlace);
    setShowTechnicalData(false);

    // ✅ Observaciones (si no viene en inst, lo buscamos)
    fetchInstrumentObsIfNeeded(existingInstrument);
  }, [existingInstrument, currentIntakeDate]);

  // modo edición: cargar instrumento maestro
  useEffect(() => {
    if (!open || !isEdit || !instrumentId || instrumentMetaSupported === null) return;

    let active = true;
    (async () => {
      try {
        const selectFields =
          instrumentMetaSupported === true
            ? "id, client_id, tag, serial, brand, model, type, category_id, unit, range_min, range_max, resolution, tolerance, is_analog, calibrationintervalmonths, baker_code, observations, reception_date, departure_date, calibration_place"
            : "id, client_id, tag, serial, brand, model, type, category_id, unit, range_min, range_max, resolution, tolerance, is_analog, calibrationintervalmonths, baker_code, observations";
        setSaving(false);
        const { data, error } = await supabase
          .from("instruments")
          .select(selectFields)
          .eq("id", instrumentId)
          .single();

        if (!active) return;
        if (error) throw error;
        if (!data) throw new Error("No se encontró el instrumento.");

        setTag(data.tag || "");
        setSerial(data.serial || "");
        setBrand(data.brand || "");
        setModel(data.model || "");
        setBakerCode(data.baker_code || "");
        setType(data.type || "pressure");
        setUnit(data.unit || "");
        setRangeMin(data.range_min != null ? String(data.range_min) : "");
        setRangeMax(data.range_max != null ? String(data.range_max) : "");
        setResolution(data.resolution != null ? String(data.resolution) : "");
        setTolerance(data.tolerance != null ? String(data.tolerance) : "");
        setIsAnalog(!!data.is_analog);
        setCalibrationIntervalMonths(
          data.calibrationintervalmonths != null
            ? String(data.calibrationintervalmonths)
            : ""
        );
        setCategoryId(data.category_id || "");
        setInstrumentClientId(data.client_id || "");
        setOriginalClientId(data.client_id || "");
        setAllowClientChange(false);
        if (instrumentMetaSupported === true) {
          setReceptionDate(data.reception_date || "");
          setDepartureDate(data.departure_date || "");
          setCalibrationPlace(data.calibration_place || "");
        } else {
          setReceptionDate("");
          setDepartureDate("");
          setCalibrationPlace("");
        }
        setInstrumentMetaDirty({
          receptionDate: false,
          departureDate: false,
          calibrationPlace: false,
        });
        setShowTechnicalData(false);

        // ✅ Observaciones persistentes
        setObservations(data.observations || "");
        setObsDirty(false);
      } catch (e) {
        console.error(e);
        if (active) alert(e.message || "No se pudo cargar el instrumento.");
      } finally {
        if (active) setSaving(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, isEdit, instrumentId, instrumentMetaSupported]);

  // 👉 cargar lista de instrumentos del intake (lado derecho)
  useEffect(() => {
    if (!open || !intakeId) return;

    let active = true;
    (async () => {
      try {
        setLoadingItems(true);
        setItemsError("");

        const { data, error } = await supabase
          .from("v_pending_intake_instruments")
          .select(
            "intake_item_id, intake_id, intake_code, lab_id, tag, serial, brand, model, intake_created_at"
          )
          .eq("intake_id", intakeId)
          .order("lab_id", { ascending: true });

        if (!active) return;
        if (error) throw error;

        const rows = Array.isArray(data) ? data : [];
        const labIds = Array.from(
          new Set(rows.map((row) => row.lab_id).filter((v) => v != null))
        );

        let placeByLabId = {};
        if (labIds.length) {
          const { data: placeRows } = await supabase
            .from("instruments")
            .select("lab_id, calibration_place")
            .in("lab_id", labIds);

          if (Array.isArray(placeRows)) {
            placeByLabId = placeRows.reduce((acc, row) => {
              acc[String(row.lab_id)] = row.calibration_place || "Laboratorio";
              return acc;
            }, {});
          }
        }

        setIntakeItems(
          rows.map((row) => ({
            ...row,
            calibration_place: placeByLabId[String(row.lab_id)] || "Laboratorio",
          }))
        );
      } catch (e) {
        console.error("[IntakeInstrumentModal] load intake items", e);
        if (active) {
          setItemsError(
            e.message || "No se pudieron cargar los instrumentos del intake."
          );
        }
      } finally {
        if (active) setLoadingItems(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, intakeId, itemsRefreshKey]);

  // 👉 quitar un instrumento del intake (solo borra intake_items, NO el maestro)
  async function handleRemoveItem(row) {
    if (!row?.intake_item_id) return;
    const ok = window.confirm(
      "¿Quitar este instrumento del intake?\n\nNo se elimina el instrumento maestro, solo se desvincula de este ingreso."
    );
    if (!ok) return;

    try {
      setLoadingItems(true);
      setItemsError("");

      const { error } = await supabase
        .from("intake_items")
        .delete()
        .eq("id", row.intake_item_id);

      if (error) throw error;

      // refrescar lista
      setItemsRefreshKey((k) => k + 1);
    } catch (e) {
      console.error("[IntakeInstrumentModal] remove intake item", e);
      setItemsError(e.message || "No se pudo quitar el instrumento del intake.");
    } finally {
      setLoadingItems(false);
    }
  }

  // guardar nuevo instrumento y vincular al intake
  async function saveNew({ addAnother = false } = {}) {
    if (!clientId) {
      alert("Falta el cliente.");
      return;
    }
    if (!intakeId) {
      alert("Falta el intake.");
      return;
    }

    const rMin = parseNum(rangeMin);
    const rMax = parseNum(rangeMax);
    const res = parseNum(resolution);
    const tol = parseNum(tolerance);

    if (rMin !== null && rMax !== null && rMin > rMax) {
      alert("El Rango mín. no puede ser mayor que el Rango máx.");
      return;
    }

    try {
      setSaving(true);

      const insertPayload = {
        client_id: clientId,
        tag: tag || null,
        serial: serial || null,
        brand: brand || null,
        model: model || null,
        baker_code: bakerCode || null,
        type,
        category_id: nullIfEmpty(categoryId),
        unit: unit || null,
        range_min: rMin,
        range_max: rMax,
        resolution: res,
        tolerance: tol,
        is_analog: isAnalog,
        calibrationintervalmonths: calibrationIntervalMonths
          ? intOrNull(calibrationIntervalMonths)
          : null,
        // ✅ NUEVO
        observations: nullIfEmpty(observations),
        reception_date: receptionDate || todayIsoDate(),
        departure_date: nullIfEmpty(departureDate),
        calibration_place: calibrationPlace || "Laboratorio",
      };

      const duplicates = await findInstrumentDuplicates(supabase, insertPayload);
      if (duplicates.length > 0) {
        window.alert(buildDuplicateInstrumentMessage(duplicates, insertPayload));
        await addExisting(duplicates[0], { addAnother });
        return;
      }

      let insertResult = await supabase
        .from("instruments")
        .insert(insertPayload)
        .select("id")
        .single();

      if (insertResult.error && shouldRetryWithoutCalibrationMeta(insertResult.error)) {
        const retryPayload = { ...insertPayload };
        delete retryPayload.reception_date;
        delete retryPayload.departure_date;
        delete retryPayload.calibration_place;
        insertResult = await supabase
          .from("instruments")
          .insert(retryPayload)
          .select("id")
          .single();
      }

      const { data, error } = insertResult;

      if (error) throw error;
      if (!data?.id) throw new Error("No se pudo crear el instrumento.");

      const instrumentIdCreated = data.id;

      const { error: linkError } = await supabase.rpc("add_instrument_to_intake", {
        p_intake_id: intakeId,
        p_instrument_id: instrumentIdCreated,
        p_note: note || null,
      });

      if (linkError) throw linkError;

      // refrescar panel de la derecha
      setItemsRefreshKey((k) => k + 1);

      if (addAnother) {
        setTag("");
        setSerial("");
        setBrand("");
        setModel("");
        setBakerCode("");
        setRangeMin("");
        setRangeMax("");
        setResolution("");
        setTolerance("");
        setIsAnalog(false);
        setCalibrationIntervalMonths("");

        setObservations("");
        setObsDirty(false);
        setReceptionDate(todayIsoDate());
        setDepartureDate("");
        setCalibrationPlace("Laboratorio");

        setNote("");
        setExistingInstrument(null);
        setCategoryId("");
      } else {
        onClose?.();
      }
    } catch (e) {
      console.error(e);
      alert(e.message || "No se pudo guardar el instrumento.");
    } finally {
      setSaving(false);
    }
  }

  async function addExisting(inst, { addAnother = false } = {}) {
    if (!intakeId) {
      alert("Falta el intake.");
      return;
    }

    try {
      setSaving(true);

      const calibrationPlaceChanged =
        (inst?.calibration_place || "Laboratorio") !== calibrationPlace;

      // En un reingreso solo se actualizan datos permanentes. Las fechas del
      // ciclo anterior no se copian ni se pisan: el nuevo ingreso queda en
      // intakes/intake_items y la salida en su remito.
      if (inst?.id && (obsDirty || calibrationPlaceChanged)) {
        const updatePayload = {
          observations: nullIfEmpty(observations),
          calibration_place: calibrationPlace || "Laboratorio",
        };

        let updateResult = await supabase
          .from("instruments")
          .update(updatePayload)
          .eq("id", inst.id);

        if (
          updateResult.error &&
          shouldRetryWithoutCalibrationMeta(updateResult.error)
        ) {
          const retryPayload = { ...updatePayload };
          delete retryPayload.calibration_place;
          updateResult = await supabase
            .from("instruments")
            .update(retryPayload)
            .eq("id", inst.id);
        }

        if (updateResult.error) throw updateResult.error;
      }

      const { error: linkError } = await supabase.rpc("add_instrument_to_intake", {
        p_intake_id: intakeId,
        p_instrument_id: inst.id,
        p_note: note || null,
      });

      if (linkError) throw linkError;

      // refrescar panel derecho
      setItemsRefreshKey((k) => k + 1);

      if (addAnother) {
        setNote("");
        // dejamos obsDirty en false para no re-escribir el maestro sin querer
        setObsDirty(false);
        setReceptionDate(todayIsoDate());
        setDepartureDate("");
        setCalibrationPlace("Laboratorio");
      } else {
        onClose?.();
      }
    } catch (e) {
      console.error(e);
      alert(e.message || "No se pudo agregar el instrumento.");
    } finally {
      setSaving(false);
    }
  }

  async function handleAdd({ addAnother = false } = {}) {
    if (isEdit) return;
    if (existingInstrument) {
      await addExisting(existingInstrument, { addAnother });
    } else {
      await saveNew({ addAnother });
    }
  }

  // finalizar e ir a imprimir constancia
  function handleFinishAndPrint() {
    if (!intakeId) return;
    const ok = window.confirm(
      "Vas a salir de la carga de instrumentos para imprimir la constancia de recepción. Luego podés volver a este intake para agregar o quitar instrumentos si es necesario. ¿Continuar?"
    );
    if (!ok) return;
    onClose?.();
    window.open(
      `/ingresos/comprobante?id=${encodeURIComponent(intakeId)}`,
      "_blank"
    );
  }

  // modo edición: actualizar instrumento maestro
  async function handleSaveEdit() {
    if (!instrumentId) return;

    const rMin = parseNum(rangeMin);
    const rMax = parseNum(rangeMax);
    const res = parseNum(resolution);
    const tol = parseNum(tolerance);

    if (rMin !== null && rMax !== null && rMin > rMax) {
      alert("El Rango mín. no puede ser mayor que el Rango máx.");
      return;
    }

    try {
      const effectiveClientId = canChangeClient
        ? instrumentClientId
        : originalClientId;
      const clientChanged =
        (effectiveClientId || "") !== (originalClientId || "");
      if (clientChanged && !criticalUnlocked) {
        if (typeof onRequestCriticalAccess === "function") {
          onRequestCriticalAccess();
          alert(
            "Para cambiar empresa/sucursal del instrumento, primero valida la contrasena de cambio critico y luego volve a presionar Guardar cambios."
          );
          return;
        }
        const okLocal = localCriticalPromptUnlock();
        if (!okLocal) return;
      }

      setSaving(true);

      const updatePayload = {
        client_id: nullIfEmpty(effectiveClientId),
        tag: tag || null,
        serial: serial || null,
        brand: brand || null,
        model: model || null,
        baker_code: bakerCode || null,
        type,
        category_id: nullIfEmpty(categoryId),
        unit: unit || null,
        range_min: rMin,
        range_max: rMax,
        resolution: res,
        tolerance: tol,
        is_analog: isAnalog,
        calibrationintervalmonths: calibrationIntervalMonths
          ? intOrNull(calibrationIntervalMonths)
          : null,
        observations: nullIfEmpty(observations),
      };

      // En edición, estos metadatos sensibles se envían únicamente si el
      // usuario los cambió. Así, editar otro dato no pisa la fecha original
      // ni el lugar de calibración con valores por defecto del formulario.
      if (instrumentMetaDirty.calibrationPlace) {
        updatePayload.calibration_place = nullIfEmpty(calibrationPlace);
      }

      let updateResult = await supabase
        .from("instruments")
        .update(updatePayload)
        .eq("id", instrumentId);

      if (updateResult.error && shouldRetryWithoutCalibrationMeta(updateResult.error)) {
        const retryPayload = { ...updatePayload };
        delete retryPayload.reception_date;
        delete retryPayload.departure_date;
        delete retryPayload.calibration_place;
        updateResult = await supabase
          .from("instruments")
          .update(retryPayload)
          .eq("id", instrumentId);
      }

      const { error } = updateResult;

      if (error) throw error;

      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      alert(e.message || "No se pudo actualizar el instrumento.");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  const totalIntakeItems = intakeItems.length;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl max-h-[90vh] bg-[#0F1115] text-white rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col">
        {/* HEADER */}
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2 flex-wrap">
              {isEdit ? "Editar datos del instrumento" : "Agregar instrumento al ingreso"}
              {isEdit && servCode && (
                <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-400/50 text-emerald-200 text-sm font-mono font-semibold">
                  {servCode}
                </span>
              )}
            </h2>
            {isEdit && servCode && (
              <p className="text-xs text-slate-300 mt-1">
                Estás editando el instrumento <b>{servCode}</b> de la orden de servicio.
              </p>
            )}
            {!isEdit && (
              <p className="text-xs text-slate-300 mt-1">
                Cliente: <b>{client?.name || "—"}</b> · Intake:{" "}
                <b>{intakeId ? String(intakeId).slice(0, 8) + "…" : "—"}</b>
              </p>
            )}
          </div>
          <button
            className="px-3 py-1.5 rounded-md border border-white/20 hover:bg-white/10 text-sm"
            onClick={onClose}
            disabled={saving}
          >
            Cerrar
          </button>
        </div>

        {/* CONTENIDO PRINCIPAL: FORM + PANEL DERECHO */}
        <div className="flex-1 overflow-auto px-5 py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* -------- FORMULARIO (2/3) -------- */}
            <div className="md:col-span-2 space-y-4">
              {/* Fecha de ingreso visible al comienzo en alta y edición */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm mb-1">Fecha de recepción</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                    value={receptionDate}
                    readOnly
                    disabled
                  />
                  <p className="mt-1 text-xs text-slate-400">
                    {isEdit
                      ? "Dato histórico de solo lectura. Cada nuevo ingreso conserva su propia fecha."
                      : "Fecha del ingreso actual. No modifica las visitas anteriores del instrumento."}
                  </p>
                </div>
              </div>

              {/* Buscar por serie/tag */}
              {!isEdit && (
                <div className="border border-white/10 rounded-lg p-3 bg-black/40">
                  <label className="block text-sm mb-1">
                    Buscar por Serie o Tag (si ya existe, se completan los datos)
                  </label>
                  <SerialTagAutocomplete
                    clientId={clientId}
                    placeholder="Escribí Serie o Tag..."
                    onPick={(inst) => {
                      setExistingInstrument(inst);
                      // por las dudas, si no trae observations, las buscamos
                      fetchInstrumentObsIfNeeded(inst);
                    }}
                    allowCreateNew={false}
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Si no existe en la lista, completá los campos inferiores
                    para crear un nuevo instrumento.
                  </p>
                </div>
              )}

              {/* Empresa / sucursal (solo edición) */}
              {isEdit && (
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <label className="block text-sm">
                        Empresa / sucursal del instrumento (cambio sensible)
                      </label>
                      {canChangeClient && (
                        <button
                          type="button"
                          className="px-2 py-1 rounded-md border border-amber-400/60 text-amber-200 hover:bg-amber-500/10 text-xs"
                          onClick={() => {
                            if (allowClientChange) return;
                            const openCritical = onRequestCriticalAccess;
                            if (typeof openCritical === "function") {
                              openCritical(() => setAllowClientChange(true));
                              return;
                            }
                            if (criticalUnlocked || localCriticalPromptUnlock()) {
                              setAllowClientChange(true);
                            }
                          }}
                        >
                          Cambiar sucursal
                        </button>
                      )}
                    </div>
                    <select
                      className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                      value={instrumentClientId || ""}
                      onChange={(e) => setInstrumentClientId(e.target.value)}
                      disabled={loadingClients || !allowClientChange}
                    >
                      <option value="">(Sin empresa asignada)</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {formatClientOption(c)}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-400 mt-1">
                      {canChangeClient
                        ? 'Se habilita solo con el botón "Cambiar sucursal" y requiere contraseña de cambio crítico.'
                        : "Tu rol no permite cambiar la empresa o sucursal del instrumento."}
                    </p>
                  </div>
                </div>
              )}

              {/* Tag / serie / código cliente */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm mb-1">Tag</label>
                  <input
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    placeholder="Identificador interno del cliente"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Serie</label>
                  <input
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                    value={serial}
                    onChange={(e) => setSerial(e.target.value)}
                    placeholder="N° de serie"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">
                    Código cliente (ej. PO Baker)
                  </label>
                  <input
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                    value={bakerCode}
                    onChange={(e) => setBakerCode(e.target.value)}
                    placeholder="Código propio del cliente (opcional)"
                  />
                </div>
              </div>

              {/* Marca / modelo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm mb-1">Marca</label>
                  <input
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    placeholder="Marca"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Modelo</label>
                  <input
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    placeholder="Modelo"
                  />
                </div>
              </div>

              {/* Variable / tipo instrumento */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm mb-1">Variable</label>
                  <select
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                    value={type}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setType(nextType);
                      const selectedCategory = categories.find(
                        (item) => item.id === categoryId
                      );
                      const allowed = selectedCategory?.variable_types || [];
                      if (allowed.length > 0 && !allowed.includes(nextType)) {
                        setCategoryId("");
                      }
                    }}
                  >
                    {TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-400 mt-1">
                    La variable se elige manualmente y determina qué tipos se
                    muestran.
                  </p>
                </div>
                <div>
                  <label className="block text-sm mb-1">Tipo de instrumento</label>
                  <select
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                    value={categoryId}
                    onChange={(e) => selectCategory(e.target.value)}
                  >
                    <option value="">(Seleccioná tipo de instrumento...)</option>
                    {categoriesForType.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-400 mt-1">
                    La lista muestra los tipos habilitados para la variable
                    seleccionada.
                  </p>
                </div>
              </div>

              {/* Datos técnicos */}
              <div className="rounded-lg border border-white/10 bg-black/30 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium">Datos técnicos</div>
                    <div className="text-xs text-slate-400">
                      Se completan en laboratorio y quedan guardados en el instrumento.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="px-3 py-1.5 rounded-md border border-amber-400/60 text-amber-200 hover:bg-amber-500/10 text-xs"
                    onClick={() => setShowTechnicalData((prev) => !prev)}
                  >
                    {showTechnicalData ? "Ocultar datos técnicos" : "Mostrar datos técnicos"}
                  </button>
                </div>

                {showTechnicalData && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm mb-1">Unidad principal</label>
                      <input
                        className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                        placeholder="Ej: bar, °C, V..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Resolución (paso)</label>
                      <input
                        className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        placeholder="Ej: 0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Mínimo</label>
                      <input
                        className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                        value={rangeMin}
                        onChange={(e) => setRangeMin(e.target.value)}
                        placeholder="Ej: 0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Máximo</label>
                      <input
                        className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                        value={rangeMax}
                        onChange={(e) => setRangeMax(e.target.value)}
                        placeholder="Ej: 100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Tolerancia (±) (opcional)</label>
                      <input
                        className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                        value={tolerance}
                        onChange={(e) => setTolerance(e.target.value)}
                        placeholder="Ej: 0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Intervalo de recalibración (meses)</label>
                      <input
                        className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                        value={calibrationIntervalMonths}
                        onChange={(e) => setCalibrationIntervalMonths(e.target.value)}
                        placeholder="Ej: 12"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={isAnalog}
                          onChange={(e) => setIsAnalog(e.target.checked)}
                        />
                        Instrumento analógico
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Salida / lugar de calibracion */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-sm mb-1">Fecha de salida</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10 opacity-70"
                      value={departureDate}
                      readOnly
                      disabled
                    />
                    <p className="mt-1 text-xs text-slate-400">
                      La salida se registra automáticamente desde el remito correspondiente.
                    </p>
                    {instrumentMetaSupported === false && (
                      <p className="mt-1 text-xs text-amber-300">
                        La base todavía no tiene esta columna; el dato no quedará guardado hasta aplicar la migración.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Lugar de calibración</label>
                    <select
                      className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                      value={calibrationPlace}
                      onChange={(e) => {
                        setCalibrationPlace(e.target.value);
                        setInstrumentMetaDirty((current) => ({
                          ...current,
                          calibrationPlace: true,
                        }));
                      }}
                    >
                      <option value="">Sin definir</option>
                      <option value="Laboratorio">Laboratorio</option>
                      <option value="In situ">In situ</option>
                    </select>
                    {instrumentMetaSupported === false && (
                      <p className="mt-1 text-xs text-amber-300">
                        La base todavía no tiene estas columnas; se muestran solo como referencia hasta aplicar la migración.
                      </p>
                    )}
                  </div>
                </div>

              {/* ✅ Observaciones del instrumento (siempre) */}
              <div className="mt-3">
                <label className="block text-sm mb-1">
                  Observaciones del instrumento (se guardan en el instrumento)
                </label>
                <textarea
                  className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                  rows={3}
                  value={observations}
                  onChange={(e) => {
                    setObservations(e.target.value);
                    setObsDirty(true);
                  }}
                  placeholder="Ej: En caja, instrumento nuevo. Falta cable. Golpe en carcasa..."
                />
                <p className="text-xs text-slate-400 mt-1">
                  Estas observaciones quedan guardadas en el instrumento maestro.
                </p>
              </div>

              {/* Nota solo en modo intake */}
              {!isEdit && (
                <div className="mt-3">
                  <label className="block text-sm mb-1">
                    Nota (para este ingreso)
                  </label>
                  <textarea
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10"
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* -------- PANEL DERECHO: INSTRUMENTOS DEL INTAKE -------- */}
            <div className="md:col-span-1">
              <div className="border border-white/10 rounded-lg bg-black/50 h-full flex flex-col">
                <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">
                      Instrumentos en este intake
                    </div>
                    <div className="text-[11px] text-slate-400">
                      Vista rápida mientras se van cargando.
                    </div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full bg-sky-500/15 text-sky-200 border border-sky-500/40">
                    {totalIntakeItems}
                  </span>
                </div>

                <div className="flex-1 overflow-auto text-xs">
                  {loadingItems && (
                    <div className="px-3 py-2 text-slate-400">
                      Cargando instrumentos…
                    </div>
                  )}
                  {itemsError && !loadingItems && (
                    <div className="px-3 py-2 text-red-400">{itemsError}</div>
                  )}
                  {!loadingItems && !itemsError && !intakeItems.length && (
                    <div className="px-3 py-2 text-slate-400">
                      Todavía no hay instrumentos en este intake.
                    </div>
                  )}

                  {intakeItems.length > 0 && (
                    <table className="min-w-full text-[11px]">
                      <thead className="sticky top-0 bg-black/70">
                        <tr>
                          <th className="px-2 py-1 text-left"></th>
                          <th className="px-2 py-1 text-left">SERV</th>
                          <th className="px-2 py-1 text-center" title="Lugar de calibracion">
                            Origen
                          </th>
                          <th className="px-2 py-1 text-left">Tag</th>
                          <th className="px-2 py-1 text-left">Descripción</th>
                          <th className="px-2 py-1 text-left">Serie</th>
                        </tr>
                      </thead>
                      <tbody>
                        {intakeItems.map((row) => {
                          const desc = [row.brand, row.model]
                            .filter(Boolean)
                            .join(" ");
                          return (
                            <tr
                              key={row.intake_item_id}
                              className="border-t border-white/5"
                            >
                              <td className="px-2 py-1 align-top">
                                <button
                                  type="button"
                                  className="text-[10px] px-2 py-1 rounded bg-red-600/80 hover:bg-red-500 text-white"
                                  onClick={() => handleRemoveItem(row)}
                                  title="Quitar de este intake"
                                >
                                  Quitar
                                </button>
                              </td>
                              <td className="px-2 py-1 align-top">
                                {row.lab_id != null
                                  ? String(row.lab_id).padStart(7, "0")
                                  : "-"}
                              </td>
                              <td className="px-2 py-1 align-top text-center">
                                <CalibrationPlaceIcon place={row.calibration_place} />
                              </td>
                              <td className="px-2 py-1 align-top">
                                {row.tag || "-"}
                              </td>
                              <td className="px-2 py-1 align-top">
                                {desc || "(sin descripción)"}
                              </td>
                              <td className="px-2 py-1 align-top">
                                {row.serial || "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ACCIONES */}
        <div className="flex justify-end gap-2 mt-2 px-5 pb-4">
          {!isEdit && (
            <button
              type="button"
              className="px-3 py-2 rounded-md border border-amber-400/70 text-amber-200 hover:bg-amber-500/10 mr-auto"
              disabled={saving || !intakeId}
              onClick={handleFinishAndPrint}
            >
              Finalizar e imprimir constancia
            </button>
          )}

          <button
            className="px-3 py-2 rounded-md border border-white/20 hover:bg-white/10"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>

          {isEdit ? (
            <button
              className="px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50"
              disabled={saving}
              onClick={handleSaveEdit}
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          ) : (
            <>
              <button
                className="px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50"
                disabled={saving || !clientId || !intakeId}
                onClick={() => handleAdd({ addAnother: true })}
              >
                {saving ? "Guardando..." : "Agregar y otro"}
              </button>
              <button
                className="px-3 py-2 rounded-md bg-sky-600 hover:bg-sky-500 disabled:opacity-50"
                disabled={saving || !clientId || !intakeId}
                onClick={() => handleAdd({ addAnother: false })}
              >
                {saving ? "Guardando..." : "Agregar"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
